import { OrganizationType } from '../hooks/useAccessBadge';

// Where `/[tenantId]/insights` forwards a viewer, or null when there's nothing
// to forward to. Pure so the permutations that have bitten the landing page — a
// teacher with no org, a viewer whose id hasn't resolved, an org whose `type` is
// missing — are testable without a session, a router, or an Unleash provider.
// A null result is what the page turns into a 404 once the access badge has
// resolved; returning null rather than a half-formed URL is what keeps a viewer
// off a never-resolving spinner.
//
// Lives here rather than beside the page because everything under `pages/` is a
// route in the Next pages router — a colocated test file there is built as a
// page and fails the production build.
export function insightsLandingDestination({
  tenantId,
  org,
  isTeacherView,
  viewerId,
  suffix,
}: {
  tenantId: string | undefined;
  org: { id: string; type?: OrganizationType } | undefined;
  isTeacherView: boolean;
  viewerId: string | null;
  suffix: string;
}): string | null {
  if (!tenantId || !org) return null;
  // Teachers always land inside their own org on their own teaching — never the
  // parent district, which their dashboard doesn't cover.
  if (isTeacherView) {
    const paths = insightsScopePaths({ tenantId, orgId: org.id, viewerId });
    return paths ? `${paths.teaching}${suffix}` : null;
  }
  const scope = scopeForOrgType(org.type);
  return scope ? `/${tenantId}/insights/${scope}/${org.id}${suffix}` : null;
}

// The two scopes a teacher's dashboard covers, as bare paths. The sidebar
// renders them as sub-items and the landing page forwards to `teaching`, so
// they resolve here rather than being spelled out at each call site — a teacher
// arriving from the nav and a teacher arriving from `/insights` must land on
// the same URL, or the sidebar's active state won't match the page they're on.
//
// Paths, NOT hrefs: the caller appends `querySuffix()` for the link. The
// sidebar also compares the current path against `school` to decide which item
// is active, and folding the suffix in here made that comparison fail for any
// non-default period, rubric or lens — the school item never lit up. Keeping
// the query out of the returned value removes that failure mode rather than
// papering over it at the comparison.
//
// Null when we can't name both: no tenant, no organization on the badge, or a
// viewer id that hasn't resolved yet. Callers hide the sub-items rather than
// rendering a half-formed link.
export function insightsScopePaths({
  tenantId,
  orgId,
  viewerId,
}: {
  tenantId: string | undefined;
  orgId: string | undefined;
  viewerId: string | null;
}): { teaching: string; school: string } | null {
  if (!tenantId || !orgId || !viewerId) return null;
  return {
    teaching: `/${tenantId}/insights/school/${orgId}/${viewerId}`,
    school: `/${tenantId}/insights/school/${orgId}`,
  };
}

const scopeForOrgType = (
  type: OrganizationType | undefined,
): 'district' | 'school' | null => {
  if (!type) return null;
  if (type === OrganizationType.District) return 'district';
  // Everything that isn't a District — School, `Other` (e.g. Curipod-source
  // demo orgs like "Curipod Texas Academy"), or any value the backend
  // started returning that we don't model yet — routes to the school view.
  // The school page handles missing/sparse data gracefully, so it's a
  // better default than stranding a viewer with a valid org on a 404.
  return 'school';
};
